import { Component, OnInit, ComponentRef } from "@angular/core";
import { MatDialog } from "@angular/material";

import { SAML, User, isSAMLUser } from "@impulse/sdp-shared-models";

import { BrowserTitleService } from "../../common/browser-title/browser-title.service";
import { ApiGatewayService } from "../../common/api-gateway/api-gateway.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { TranslateService } from "../../common/translate/translate.service";
import { SnackbarService } from "../../common/snackbar/snackbar.service";
import { SamlWizardService, SamlSelectIdpComponent } from "./saml-wizard";
import { UserWizardService } from "./user-wizard/user-wizard.service";
import { UserWizardEditComponent } from "./user-wizard/user-wizard-edit/user-wizard-edit.component";
import { WizardDialogService } from "../wizard-dialog/wizard-dialog.service";
import { getAccountName } from "../../util";
import { AuthenticationService } from "../../common/authentication/authentication.service";
import { tableDeleteIcon, tableEditIcon } from "../../util";
import { GenericTable } from "../../genericTable";
import { SdpIcon } from "../../common/icon/models";
import { CustomUserEmailComponent } from "./customUserEmail/custom-user-email.component";
import { SamlAliasesService } from "../saml-aliases";

@Component({
	selector: "sdp-users",
	templateUrl: "./users.component.html"
})
export class UsersComponent implements OnInit {
	public readonly userSchema: GenericTable.ColumnSchema[] = [
		{
			minWidth: "30%",
			type: "customComponent",
			headerSchema: {
				type: "basic",
				sortable: false,
				headerText: "form.label.email"
			},
			bodySchema: {
				type: "customComponent",
				customComponent: CustomUserEmailComponent,
				callback: (component: ComponentRef<CustomUserEmailComponent>, data: User) => {
					component.instance.username = data.email;
					component.instance.isDisabled = data.disabled;
				}
			}
		}, {
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "form.label.firstname"
			},
			bodySchema: {
				type: "string",
				boundField: "firstName"
			}
		}, {
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "form.label.lastname"
			},
			bodySchema: {
				type: "string",
				boundField: "lastName"
			}
		}, {
			minWidth: "10%",
			type: "icon",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "form.label.isAdmin"
			},
			bodySchema: {
				type: "icon",
				icon: { type: "material", name: "check", cssClass: "sdp-user-is-admin-checked-icon" },
				showIcon: (row: User) => { return this.showAdminIcon(row); }
			}
		}, {
			minWidth: "10%",
			type: "button",
			headerSchema: {
				type: "none",
			},
			bodySchema: {
				type: "button",
				buttons: this.getUserButtons()
			}
		}];

	public readonly samlSchema: GenericTable.ColumnSchema[] = [
		{
			minWidth: "25%",
			type: "string",
			headerSchema: {
				type: "basic",
				sortable: true,
				headerText: "form.label.identityProvider"
			},
			bodySchema: {
				type: "string",
				boundField: "name"
			}
		}, {
			minWidth: "65%",
			type: "tag",
			headerSchema: {
				type: "basic",
				headerText: "form.label.attributes",
				sortable: true
			},
			bodySchema: {
				type: "tag",
				boundField: "roleAttributes"
			}
		}, {
			minWidth: "10%",
			type: "button",
			headerSchema: {
				type: "none",
			},
			bodySchema: {
				type: "button",
				buttons: this.getSamlButtons()
			}
		}];

	public data: User[] | undefined = undefined;
	public samlData: SAML.IdentityProvider[] | undefined = undefined;
	public isDataLoaded = false;

	private _showProgressBar = false;
	private accountName = getAccountName();

	public rowSchema: GenericTable.RowSchema = {
		cssClass: (row: User) => { return row.disabled ? "sdp-generic-row-disabled" : ""; },
		tooltip: (row: User) => { return row.disabled ? "user.disabled.tooltip" : ""; }
	};

	constructor(
		public dialog: MatDialog,
		private titleService: BrowserTitleService,
		private apiGatewayService: ApiGatewayService,
		private translateService: TranslateService,
		private snackbarService: SnackbarService,
		private samlService: SamlWizardService,
		private userWizardService: UserWizardService,
		private wizardDialogService: WizardDialogService,
		private auth: AuthenticationService,
		private aliasService: SamlAliasesService
	) {
		samlService.identityProvidersSubject.subscribe((value) => {
			this.samlData = value;
		});
	}

	public async ngOnInit() {
		this.titleService.setTitle("users.header.title");
		const samlP = this.samlService.getIdentityProviders();
		const userP = this.refresh();

		this.samlData = await samlP;
		await userP;
	}

	public get showProgressBar() {
		return this._showProgressBar;
	}

	public createSamlDialog() {
		this.samlService.newIdentityProvider();
		this.openSamlDialog(true);
	}

	public editSamlDialog(idp: SAML.IdentityProvider) {
		this.samlService.editIdentityProvider(idp);
		this.openSamlDialog(false);
	}

	public async deleteSamlDialog(id: string, idpName: string) {
		let message: string = this.translateService.translate("saml.delete.idp.dialog.message", { idpName: idpName });
		if (await this.hasSamlAlias(id)) {
			message += this.translateService.translate("saml.delete.idp.dialog.message.has-alias");
		}

		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
		dialogRef.componentInstance.message = message;
		dialogRef.componentInstance.title = "saml.delete.idp.dialog.title";
		dialogRef.componentInstance.buttonAction = "sdp.label.delete";
		dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";
		dialogRef.afterClosed().subscribe(async (result: boolean) => {
			if (result) {
				try {
					await this.samlService.deleteIdentityProvider(id);
					this.snackbarService.open(this.translateService.translate("saml.delete.idp.success", { idpName: idpName }));
				} catch (error) {
					this.snackbarService.open(this.translateService.translate(error));
				}
			}
		});
	}

	public createUserDialog() {
		this.userWizardService.newUser();
		this.openUserDialog(true);
	}

	public editUserDialog(user: User) {
		this.userWizardService.editUser(user);
		this.openUserDialog(false);
	}

	public async deleteUser(user: User) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
		dialogRef.componentInstance.message = this.translateService.translate(
			"users.delete.dialog.message",
			{
				email: user.email
			}
		);
		dialogRef.componentInstance.title = "users.delete.dialog.title";
		if (user.admin) {
			const currentUser = this.auth.getCurrentUser();
			if (currentUser !== undefined && user.email === currentUser.email) {
				dialogRef.componentInstance.message = this.translateService.translate("users.delete.dialog.message.self");
				dialogRef.componentInstance.title = "users.delete.dialog.title.self";
				dialogRef.componentInstance.confirmationMessage = "users.delete.dialog.confirmation.self";
			} else {
				dialogRef.componentInstance.title = "users.delete.dialog.title.admin";
				dialogRef.componentInstance.confirmationMessage = "users.delete.dialog.confirmation";
			}
			dialogRef.componentInstance.confirmationPlaceholder = "form.label.email";
			dialogRef.componentInstance.confirmValue = (email) => email === user.email;
		}
		dialogRef.componentInstance.buttonAction = "sdp.label.delete";
		dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";
		dialogRef.afterClosed().subscribe(async (result: boolean) => {
			if (result) {
				try {
					this._showProgressBar = true;
					const deleted = await this.apiGatewayService.deleteUser(user.email);
					this.data = deleted;
					this._showProgressBar = false;
					const currentUser = this.auth.getCurrentUser();
					if (currentUser !== undefined && user.email === currentUser.email) {
						this.auth.logout();
					}
				} catch (error) {
					this.snackbarService.open(this.translateService.translate(error));
				}
			}
		});
	}

	public sendEmailDialog(user: User) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {});
		dialogRef.componentInstance.message = this.translateService.translate(
			"users.sendEmail.dialog.message",
			{
				email: user.email
			}
		);
		dialogRef.componentInstance.title = "users.sendEmail.dialog.title";
		dialogRef.componentInstance.buttonAction = "sdp.label.send";
		dialogRef.componentInstance.buttonCancel = "sdp.label.cancel";
		dialogRef.afterClosed().subscribe(async (result: boolean) => {
			if (result) {
				try {
					await this.apiGatewayService.resendUserInvitation(user.email, this.accountName);
					this.snackbarService.open(this.translateService.translate(
						"users.sendEmail.success.message",
						{
							email: user.email
						}
					));
				} catch (error) {
					this.snackbarService.open(this.translateService.translate(error));
				}
			}
		});
	}

	public showButton(row: User): boolean {
		return !row.primary;
	}

	public showAdminIcon(row: User): boolean {
		return row.admin;
	}

	private async hasSamlAlias(id: SAML.IdentityProvider.Id) {
		try {
			const aliases = await this.apiGatewayService.getAllSamlAliases();
			const index = aliases.findIndex((alias) => {
				return alias.providerId === id;
			});
			return index >= 0;

		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		}
	}

	private openSamlDialog(isCreate: boolean) {
		const dialogRef = this.wizardDialogService.openDialog(
			SamlSelectIdpComponent,
			{
				type: "material", name: "lock_open", cssClass: "sdp-section-header-icon",
				logo: { name: "SAML", cssClass: "sdp-saml-logo-icon" }
			},
			isCreate
		);
		dialogRef.afterClosed().subscribe(async () => {
			await this.refresh();
		});
	}

	private openUserDialog(isCreate: boolean) {
		const dialogRef = this.wizardDialogService.openDialog(
			UserWizardEditComponent,
			{ type: "material", name: "person", cssClass: "sdp-section-header-icon" },
			isCreate
		);
		dialogRef.afterClosed().subscribe(async () => {
			await this.refresh();
		});
	}

	private async refresh() {
		this.isDataLoaded = false;
		try {
			this._showProgressBar = true;
			this.data = await this.apiGatewayService.getAllUsers();
		} catch (e) {
			this.snackbarService.open(this.translateService.translate(e));
		} finally {
			this.isDataLoaded = true;
			this._showProgressBar = false;
		}
	}

	private getSamlButtons() {
		const buttons: GenericTable.Button[] = [];
		buttons.push({
			type: "icon",
			icon: tableEditIcon,
			onClick: (row: SAML.IdentityProvider) => { return this.editSamlDialog(row); }
		});
		buttons.push({
			type: "icon",
			icon: tableDeleteIcon,
			onClick: (row: SAML.IdentityProvider) => { return this.deleteSamlDialog(row.id, row.name); }
		});
		return buttons;
	}

	private getUserButtons() {
		const buttons: GenericTable.Button[] = [];
		buttons.push({
			type: "icon",
			icon: tableEditIcon,
			onClick: (row: User) => { return this.editUserDialog(row); }
		});
		const emailIcon: SdpIcon.Icon = { type: "material", name: "email", cssClass: "sdp-icon-button-icon" };
		buttons.push({
			type: "icon",
			icon: emailIcon,
			onClick: (row: User) => { return this.sendEmailDialog(row); },
			showButton: (row: User) => { return this.showButton(row); }
		});
		buttons.push({
			type: "icon",
			icon: tableDeleteIcon,
			onClick: (row: User) => { return this.deleteUser(row); },
			showButton: (row: User) => { return this.showButton(row); }
		});
		return buttons;
	}
}
